import * as React from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, CircleInfo } from "@/components/icons";

// ==========================================================================
// InfoCard — 접이식 카드 섹션
// ==========================================================================
export interface InfoCardProps {
  /** 제목 왼쪽 아이콘 (20px) */
  icon: React.ReactNode;
  title: string;
  /** 처음 열림 여부. 기본 열림 */
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * 조회 드로어의 접이식 카드 섹션. `InfoCard.Row` · `Divider` · `Notice` 로 조립한다.
 * 높이를 재서 ease-in-out 으로 보간한다 — Radix Collapsible 은 닫힐 때 내용을 숨겨 트랜지션이 안 걸린다.
 */
export function InfoCard({
  icon,
  title,
  defaultOpen = true,
  children,
  className,
}: InfoCardProps) {
  const [open, setOpen] = React.useState(defaultOpen);
  const contentId = React.useId();
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = React.useState<number | null>(null);

  // 내용 높이가 바뀔 때(스켈레톤 → 값) 따라가야 하므로 매 렌더 측정한다. 동등성 검사가 루프를 끊는다
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useLayoutEffect(() => {
    const node = contentRef.current;
    if (!node) return;
    const next = node.offsetHeight;
    setContentHeight((prev) => (prev === next ? prev : next));
  });

  return (
    <div
      className={cn(
        "flex w-full flex-col overflow-hidden rounded-2xl border border-lines-strong",
        className,
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full cursor-pointer items-center gap-3 px-5 pt-5 pb-4 text-left"
      >
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="flex shrink-0">{icon}</span>
          <span className="min-w-0 flex-1 truncate typo-heading-2-semibold text-labels-primary">
            {title}
          </span>
        </div>
        <span
          className={cn(
            "flex shrink-0 transition-transform duration-300 ease-in-out",
            open && "rotate-180",
          )}
        >
          <ChevronDown size={24} color="labels-alternative" />
        </span>
      </button>
      <div
        id={contentId}
        className={cn(
          "overflow-hidden",
          // 첫 측정 전에는 트랜지션 없이 그린다
          contentHeight !== null &&
            "transition-[height] duration-300 ease-in-out",
        )}
        style={{ height: open ? (contentHeight ?? undefined) : 0 }}
        inert={!open}
      >
        {/* 구분선을 본문 쪽에 둬 접힐 때 함께 잘려 나가게 한다 */}
        <div
          ref={contentRef}
          className="flex flex-col gap-2 border-t border-lines-strong bg-fills-light px-5 pt-4 pb-5"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// InfoCard.Row — 라벨 ······ 값 단위
// ==========================================================================
export interface InfoCardRowProps {
  /** 라벨 왼쪽 아이콘 (16px) */
  icon: React.ReactNode;
  label: string;
  /** 라벨 옆 보조 설명 (예: `34.562kWh × 290원`) */
  sub?: React.ReactNode;
  value?: React.ReactNode;
  /** 값 뒤 단위 (kWh · 원 · 분) */
  unit?: string;
  /** 단위 뒤 부가 표기 (예: `(1시간 12분)`) */
  note?: React.ReactNode;
  /** 핵심 값 강조 — 큰 초록 숫자 */
  emphasis?: boolean;
  isLoading?: boolean;
}

/** 빈 문자열도 값 없음으로 본다. 숫자 0 은 값이다 */
function isBlank(value: React.ReactNode): boolean {
  return value == null || value === "";
}

function InfoCardRow({
  icon,
  label,
  sub,
  value,
  unit,
  note,
  emphasis = false,
  isLoading = false,
}: InfoCardRowProps) {
  const blank = isBlank(value);

  return (
    <div className="flex min-h-6 w-full items-center gap-4">
      <div className="flex shrink-0 items-center gap-1.5">
        <span className="flex shrink-0">{icon}</span>
        <span className="whitespace-nowrap typo-body-2-medium text-labels-alternative">
          {label}
        </span>
        {sub && (
          <span className="whitespace-nowrap typo-body-2-medium text-labels-assistive">
            {sub}
          </span>
        )}
      </div>

      <div className="min-w-4 flex-1 border-t border-dotted border-lines-normal" />

      {isLoading ? (
        <Skeleton className="h-4 w-24 rounded" />
      ) : (
        <div
          className={cn(
            "flex shrink-0 items-baseline gap-0.5 whitespace-nowrap",
            emphasis
              ? "typo-heading-1-semibold text-primary-normal"
              : "typo-body-2-medium text-labels-primary",
          )}
        >
          <span>{blank ? "-" : value}</span>
          {!blank && unit && (
            <span
              className={cn(
                "text-labels-alternative",
                emphasis ? "typo-heading-1-medium" : "typo-body-2-medium",
              )}
            >
              {unit}
            </span>
          )}
          {!blank && note && (
            <span className="ml-0.5 typo-body-1-medium text-labels-alternative">
              {note}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// InfoCard.Divider — 행 묶음 사이 구분선
// ==========================================================================
function InfoCardDivider() {
  return (
    <div className="flex h-6 w-full items-center">
      <div className="w-full border-t border-lines-strong" />
    </div>
  );
}

// ==========================================================================
// InfoCard.Notice — 값 아래 회색 안내
// ==========================================================================
export interface InfoCardNoticeProps {
  children?: React.ReactNode;
}

/** 문구가 없으면 그리지 않는다 */
function InfoCardNotice({ children }: InfoCardNoticeProps) {
  if (isBlank(children)) return null;

  return (
    <Badge
      variant="none"
      size="md"
      className="w-full justify-start gap-1 whitespace-normal text-left"
    >
      <span className="flex shrink-0">
        <CircleInfo size={14} color="labels-alternative" />
      </span>
      <span className="min-w-0">{children}</span>
    </Badge>
  );
}

InfoCard.Row = InfoCardRow;
InfoCard.Divider = InfoCardDivider;
InfoCard.Notice = InfoCardNotice;
